/**
 * Attachment Upload Utility for Web
 * Handles both image and document uploads to S3 for bulletin board posts
 */

import { generateClient } from 'aws-amplify/api';

const client = generateClient();

export interface AttachmentMetadata {
  id: string;
  url: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  isImage: boolean;
}

export interface UploadAttachmentOptions {
  file: File;
  onProgress?: (progress: number) => void;
  maxRetries?: number;
}

const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024; // 10MB

// Allowed file types based on requirements
const ALLOWED_FILE_TYPES = [
  // Images
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/tiff',
  'image/svg+xml',
  'image/heic',
  'image/heif',
  // Documents
  'application/pdf',
  'application/msword', // DOC
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
  'application/vnd.ms-excel', // XLS
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // XLSX
  'application/vnd.ms-powerpoint', // PPT
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // PPTX
  'text/plain', // TXT
  'text/rtf', // RTF
  'application/rtf', // RTF (alternative)
  'text/csv', // CSV
  'application/csv', // CSV (alternative)
];

const IMAGE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/tiff',
  'image/svg+xml',
  'image/heic',
  'image/heif',
];

/**
 * Validate attachment before upload
 */
export function validateAttachment(file: File): { valid: boolean; error?: string } {
  // Validate file name
  if (!file.name || file.name.trim().length === 0) {
    return {
      valid: false,
      error: 'File name is required',
    };
  }

  // Validate file type
  const normalizedType = file.type.toLowerCase();
  if (!ALLOWED_FILE_TYPES.includes(normalizedType)) {
    return {
      valid: false,
      error: 'Unsupported file type. Allowed: JPEG, JPG, PNG, GIF, WebP, BMP, TIFF, SVG, HEIC, PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX, TXT, RTF, CSV',
    };
  }

  // Validate file size
  if (file.size <= 0) {
    return {
      valid: false,
      error: 'File size must be greater than 0',
    };
  }

  if (file.size > MAX_ATTACHMENT_SIZE) {
    return {
      valid: false,
      error: `File size exceeds maximum of 10MB. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`,
    };
  }

  return { valid: true };
}

/**
 * Generate pre-signed S3 URL for bulletin attachment upload
 */
async function getPresignedUploadUrl(
  fileName: string,
  fileType: string,
  fileSize: number
): Promise<{ uploadUrl: string; s3Key: string; publicUrl: string }> {
  const mutation = `
    mutation GetBulletinAttachmentUploadUrl($input: GetBulletinAttachmentUploadUrlInput!) {
      getBulletinAttachmentUploadUrl(input: $input) {
        uploadUrl
        s3Key
        publicUrl
      }
    }
  `;

  const response: any = await client.graphql({
    query: mutation,
    variables: {
      input: {
        fileName,
        fileType,
        fileSize,
      },
    },
  });

  return response.data.getBulletinAttachmentUploadUrl;
}

/**
 * Upload file to S3 with retry logic
 */
async function uploadToS3(
  uploadUrl: string,
  file: File,
  onProgress?: (progress: number) => void,
  maxRetries: number = 3
): Promise<void> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      // Create XMLHttpRequest for progress tracking
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        // Track upload progress
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable && onProgress) {
            const progress = Math.round((e.loaded / e.total) * 100);
            onProgress(progress);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            if (onProgress) {
              onProgress(100);
            }
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed due to network error'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload was aborted'));
        });

        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      return; // Success
    } catch (error) {
      lastError = error as Error;
      console.error(`Upload attempt ${attempt + 1} failed:`, error);

      // Wait before retrying (exponential backoff)
      if (attempt < maxRetries - 1) {
        await new Promise((resolve) => setTimeout(resolve, Math.pow(2, attempt) * 1000));
      }
    }
  }

  throw lastError || new Error('Upload failed after all retries');
}

/**
 * Upload attachment for bulletin board post
 * Returns attachment metadata including S3 URL
 */
export async function uploadBulletinAttachment(
  options: UploadAttachmentOptions
): Promise<AttachmentMetadata> {
  const { file, onProgress, maxRetries = 3 } = options;

  // Validate attachment
  const validation = validateAttachment(file);
  if (!validation.valid) {
    throw new Error(validation.error);
  }

  try {
    // Generate unique ID for this attachment
    const attachmentId = `${Date.now()}-${Math.random().toString(36).substring(7)}`;

    // Get pre-signed upload URL
    const { uploadUrl, publicUrl } = await getPresignedUploadUrl(
      file.name,
      file.type,
      file.size
    );

    // Upload to S3
    await uploadToS3(uploadUrl, file, onProgress, maxRetries);

    // Determine if this is an image
    const isImage = IMAGE_TYPES.includes(file.type.toLowerCase());

    // Return attachment metadata
    return {
      id: attachmentId,
      url: publicUrl,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      isImage,
    };
  } catch (error) {
    console.error('Error uploading bulletin attachment:', error);
    throw error;
  }
}

/**
 * Upload multiple attachments in parallel (max 3 concurrent)
 */
export async function uploadBulletinAttachments(
  files: File[],
  onProgress?: (fileIndex: number, progress: number) => void
): Promise<AttachmentMetadata[]> {
  const MAX_CONCURRENT = 3;
  const results: AttachmentMetadata[] = [];
  
  // Process files in batches
  for (let i = 0; i < files.length; i += MAX_CONCURRENT) {
    const batch = files.slice(i, i + MAX_CONCURRENT);
    const batchPromises = batch.map((file, batchIndex) => {
      const fileIndex = i + batchIndex;
      return uploadBulletinAttachment({
        file,
        onProgress: (progress) => onProgress?.(fileIndex, progress),
      });
    });
    
    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults);
  }

  return results;
}
